import React, { useCallback } from 'react';
import { PricingItem, CurrencyObject } from 'common/types';

import { Box } from '@mui/material';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import createStyles from '@mui/styles/createStyles';
import PricingTableRow from 'views/Product/__components/PricingTableRow';
import PricingTableRowAdd from 'views/Product/__components/PricingTableRowAdd';
import { sortPricingItems } from 'common/utils/pricing';

interface Props {
	items: PricingItem[];
	onChange: (items: PricingItem[]) => void;
	basePrice: number;
	currency: CurrencyObject;
	locked?: boolean;
	hidePrice?: boolean;
	hideMultiplier?: boolean;
	priceIsExample?: boolean;
	disabled?: boolean;
}

const PricingTableBody = (props: Props) => {
	const {
		items,
		onChange,
		basePrice,
		currency,
		locked,
		hidePrice,
		hideMultiplier,
		priceIsExample,
	} = props;
	const classes = useStyles();

	const handleRowChange = useCallback(
		(index: number, newItem: PricingItem) => {
			onChange(
				items
					.map((item, idx) => {
						if (idx === index) {
							return newItem;
						}
						return item;
					})
					.sort(sortPricingItems),
			);
		},
		[items, onChange],
	);

	const handleRowDelete = useCallback(
		(index: number) => {
			onChange(items.filter((_, idx) => idx !== index));
		},
		[items, onChange],
	);

	const handleRowAdd = useCallback(
		(item: PricingItem) => {
			onChange(items.concat(item).sort(sortPricingItems));
		},
		[items, onChange],
	);

	return (
		<div>
			<div className={classes.scrollWrapper}>
				<div className={classes.scrollableArea}>
					{items.map((item, index) => (
						<PricingTableRow
							key={index}
							item={item}
							index={index}
							basePrice={basePrice}
							currency={currency}
							onChange={handleRowChange}
							onDelete={handleRowDelete}
							locked={locked}
							hidePrice={hidePrice}
							hideMultiplier={hideMultiplier}
							priceIsExample={priceIsExample}
						/>
					))}
				</div>
			</div>
			{!locked && (
				<Box p={2} display="flex" flexDirection="row" justifyContent="space-between">
					<PricingTableRowAdd
						onAdd={handleRowAdd}
						defaultValues={{
							timeValue: (items?.[items.length - 1]?.timeValue || 0) + 1,
							timePeriod: items?.[items.length - 1]?.timePeriod,
							price: basePrice,
							multiplier: 1,
						}}
					/>
				</Box>
			)}
		</div>
	);
};

const useStyles = makeStyles((theme: Theme) =>
	createStyles({
		scrollWrapper: {
			width: '100%',
			overflowX: 'auto',
		},
		scrollableArea: {
			minWidth: theme.breakpoints.values.md,
			padding: theme.spacing(2),
			borderTop: `1px solid ${theme.palette.border.paper}`,
			borderBottom: `1px solid ${theme.palette.border.paper}`,
		},
	}),
);

export default PricingTableBody;
